/*eslint-disable*/
import React, { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'

import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import UploadSiteFiles from 'components/SitesUploads'
import Uploader from '../../../About/uploader'
import { requestUpdateEcosystemFiles, requestDeleteEcosystemFiles, requestGetEcosystemFiles } from 'api/ecoSystem'
import {
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_SUPERSEDED_ID,
  SITE_STATUS_DELISTED_ID,
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
} from 'constants/siteStatuses'
import { showErrorToast } from 'utils/toast'

/**
 * EcosystemDocsUploader Component
 * 
 * Behavior Requirements:
 * 1. Visible during all phases of proposal (draft to confirmation)
 * 2. Following confirmation, documents should NOT be visible alongside the assessment
 * 3. On reassessment, proposers get blank slate (can upload fresh documentation)
 * 4. Enable it if there is one or more ecosystems assessments for the site
 * 5. Grey it out unless any ecosystems are listed
 * 
 * Implementation:
 * - Files are visible to Proposers/NCG only up to confirmation (not after)
 * - Upload/delete are disabled when site is confirmed or not in draft/edit-only status
 * - During reassessment, proposers get blank slate (new files for new assessment)
 */
const EcosystemDocsUploader = ({ site, ecosystems = [] }) => {
  const params = useParams()

  const siteId = site?.id || params.id
  const siteHistoryId = params.siteHistoryId || site?.sitehistoryId || null
  const isReassessment = Boolean(siteHistoryId)
  const ecosystemsCount = ecosystems?.length ?? 0
  const hasEcosystems = ecosystemsCount > 0

  // Site status checks
  const isConfirmedOrLater = [
    SITE_STATUS_CONFIRMED_ID,
    SITE_STATUS_SUPERSEDED_ID,
    SITE_STATUS_DELISTED_ID
  ].includes(site.SiteStatusId)

  // Visibility logic:
  // - Proposers/NCG: visible only up to confirmation (not after)
  // - Not visible after confirmation
  const shouldShow = hasEcosystems && !isConfirmedOrLater

  // Upload/delete should be disabled when:
  // 1. Site is confirmed or later (read-only for proposers)
  // 2. Site is not in draft or edit-only status (no editing allowed)
  // 3. No ecosystems are listed (greyed out)
  const editableStatuses = [SITE_STATUS_DRAFT_ID, SITE_STATUS_EDIT_ONLY_ID, SITE_STATUS_SUBMITTED_EDIT_ONLY_ID]
  const disabled = !editableStatuses.includes(site.SiteStatusId) || isConfirmedOrLater || !hasEcosystems

  // For proposers during reassessment: blank slate (don't show previous assessment files)
  const shouldFetchFiles = !isReassessment

  const uploaderParams = useMemo(() => {
    return {
      id: siteId,
      siteHistoryId: shouldFetchFiles ? siteHistoryId : null // Blank slate for proposers on reassessment
    }
  }, [siteId, siteHistoryId, shouldFetchFiles])

  // File helper functions
  function normFile(f) {
    const n = { ...f }
    n.id = n.id ?? n.file_id ?? null
    n.name = n.name ?? n.file_name ?? n.originalFilename ?? n.originalName ?? ""
    n.blob_name = n.blob_name ?? n.blobName ?? n.id ?? n.file_id ?? ""
    n.fileUrl = n.fileUrl ?? n.url ?? n.sasUrl ?? n.download_url ?? ""
    const blob = n.blob_name ?? n.blobName
    n.bareUrl = n.bareUrl ?? (blob ? `https://wdkbasitesfiles.blob.core.windows.net/sites-files/${blob}` : "")
    n.url = n.fileUrl || n.bareUrl || ""
    n.downloadUrl = n.fileUrl || n.url
    n.size = n.size ?? null
    n.mime = n.mime ?? n.mimetype ?? n.contentType ?? ""
    n.fileExtension = n.fileExtension ?? n.extension ?? null
    n.uploadedBy = n.uploadedBy ?? null
    return n
  }

  function uniqByKey(arr) {
    const seen = new Set()
    return (arr || []).filter((x) => {
      const n = normFile(x)
      const k = n.blob_name || n.url || n.id || ""
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
  }

  function extractFiles(res) {
    if (!res) return []
    if (Array.isArray(res)) return res
    if (Array.isArray(res.files)) return res.files
    if (Array.isArray(res.data?.files)) return res.data.files
    if (Array.isArray(res.data)) return res.data
    if (Array.isArray(res.results)) return res.results
    return []
  }

  // Don't render if no ecosystems or not visible
  if (!siteId || !shouldShow) return null

  return (
    <Container>
      <Row>
        <Col md={8} mdOffset={2}>
          <div style={{ marginBottom: '12px' }}>
            <UploadSiteFiles
              label="uploadEcosystemsDocumentation"
              field="uploadEcosystemsDocumentation"
              infoTooltip="uploadEcosystemsDocumentationHint"
              height="128px"
              dataTest="upload-ecosystem-docs"
              isOptional={true}
              disabled={disabled || !hasEcosystems}
            />
          </div>
          <div style={{ padding: '8px 12px 12px 12px' }}>
            <Uploader
              key={`${uploaderParams.id || 'site'}-${uploaderParams.siteHistoryId || 'assessment'}-ecosystems-${hasEcosystems}`}
              params={uploaderParams}
              siteFiles={[]}
              disabled={disabled}
              uploadFn={async (uploadSiteId, uploadSiteHistoryId, formData, options) => {
                try {
                  // Ensure siteId is a primitive value
                  const actualSiteId = typeof uploadSiteId === 'object' ? (uploadSiteId?.id || uploadSiteId?.siteId) : uploadSiteId
                  const actualHistoryId = typeof uploadSiteHistoryId === 'object' ? (uploadSiteHistoryId?.siteHistoryId || uploadSiteHistoryId?.historyId) : uploadSiteHistoryId
                  
                  const response = await requestUpdateEcosystemFiles(actualSiteId, actualHistoryId, formData, options)
                  
                  let nextFiles = extractFiles(response).map(normFile)
                  if (!nextFiles.length) {
                    let res
                    try {
                      res = isReassessment && shouldFetchFiles
                        ? await requestGetEcosystemFiles(actualSiteId, actualHistoryId)
                        : await requestGetEcosystemFiles(actualSiteId, null)
                    } catch {
                      res = isReassessment && shouldFetchFiles
                        ? await requestGetEcosystemFiles(actualSiteId, actualHistoryId)
                        : await requestGetEcosystemFiles(actualSiteId, null)
                    }
                    nextFiles = extractFiles(res).map(normFile)
                  }
                  const merged = uniqByKey(nextFiles)
                  return response
                } catch (error) {
                  showErrorToast(<FormattedMessage id="uploadFailed" />)
                  throw error
                }
              }}
              deleteFn={async (deleteSiteId, deleteSiteHistoryId, fileId) => {
                try {
                  // Ensure siteId is a primitive value
                  const actualSiteId = typeof deleteSiteId === 'object' ? (deleteSiteId?.id || deleteSiteId?.siteId) : deleteSiteId
                  const actualHistoryId = typeof deleteSiteHistoryId === 'object' ? (deleteSiteHistoryId?.siteHistoryId || deleteSiteHistoryId?.historyId) : deleteSiteHistoryId
                  
                  const response = await requestDeleteEcosystemFiles(actualSiteId, actualHistoryId, fileId)
                  let nextFiles = extractFiles(response).map(normFile)
                  if (!nextFiles.length) {
                    let res
                    try {
                      res = isReassessment && shouldFetchFiles
                        ? await requestGetEcosystemFiles(actualSiteId, actualHistoryId)
                        : await requestGetEcosystemFiles(actualSiteId, null)
                    } catch {
                      res = isReassessment && shouldFetchFiles
                        ? await requestGetEcosystemFiles(actualSiteId, actualHistoryId)
                        : await requestGetEcosystemFiles(actualSiteId, null)
                    }
                    nextFiles = extractFiles(res).map(normFile)
                  }
                  const merged = uniqByKey(nextFiles)
                  return response
                } catch (error) {
                  showErrorToast(<FormattedMessage id="deleteFailed" />)
                  throw error
                }
              }}
              fetchFilesFn={async (fetchSiteId, fetchSiteHistoryId) => {
                try {
                  // For proposers during reassessment: return empty (blank slate)
                  if (isReassessment && !shouldFetchFiles) {
                    return { files: [] }
                  }

                  // Ensure siteId is a primitive value
                  const actualSiteId = typeof fetchSiteId === 'object' ? (fetchSiteId?.id || fetchSiteId?.siteId) : fetchSiteId
                  const actualHistoryId = typeof fetchSiteHistoryId === 'object' ? (fetchSiteHistoryId?.siteHistoryId || fetchSiteHistoryId?.historyId) : fetchSiteHistoryId
                  
                  let res
                  try {
                    res = isReassessment && actualHistoryId && shouldFetchFiles
                      ? await requestGetEcosystemFiles(actualSiteId, actualHistoryId)
                      : await requestGetEcosystemFiles(actualSiteId, null)
                  } catch {
                    res = isReassessment && actualHistoryId && shouldFetchFiles
                      ? await requestGetEcosystemFiles(actualSiteId, actualHistoryId)
                      : await requestGetEcosystemFiles(actualSiteId, null)
                  }
                  return res
                } catch (error) {
                  console.error('Failed to fetch ecosystem files:', error)
                  return { files: [] }
                }
              }}
              fileType="ecosystems"
              hideDownload={isConfirmedOrLater} // Hide downloads after confirmation
            />
          </div>
        </Col>
      </Row>
    </Container>
  )
}

export default EcosystemDocsUploader
